import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFetch } from '../hooks/useFetch';
import { merchant as merchantApi } from '../services/api';
import { PageLoader } from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

function CopyField({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex items-center gap-2">
        <div className={`flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm ${mono ? 'font-mono text-xs' : ''} text-gray-300 break-all`}>
          {value || '—'}
        </div>
        {value && (
          <button
            onClick={handleCopy}
            className="flex-shrink-0 btn-secondary text-xs px-3 py-2 h-full"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { merchant } = useAuth();
  const { data, loading, error, refetch } = useFetch(merchantApi.settings);

  if (loading) return <PageLoader />;
  if (error) return (
    <div className="p-8">
      <ErrorMessage message={error} onRetry={refetch} />
    </div>
  );

  // Backend returns { merchant: {...} }; fall back to auth context merchant
  const settings = data?.merchant || merchant || {};

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-sm text-gray-400 mt-1">Manage your merchant profile and configuration</p>
      </div>

      {/* Merchant Profile */}
      <section className="card mb-6">
        <h2 className="text-base font-semibold text-white mb-4">Merchant Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Business Name</label>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300">
              {settings.name || '—'}
            </div>
          </div>
          <div>
            <label className="label">Email</label>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300">
              {settings.email || '—'}
            </div>
          </div>
          <div>
            <label className="label">Account Status</label>
            <div className="flex items-center gap-2 mt-1">
              <div className={`w-2 h-2 rounded-full ${settings.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
              <span className="text-sm text-gray-300 capitalize">{settings.status || 'unknown'}</span>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-800">
          <p className="text-xs text-gray-500">
            To update your business name or email, contact <span className="text-blue-400">support@mintbridge.cloud</span>.
          </p>
        </div>
      </section>

      {/* Wallet & Contracts */}
      <section className="card mb-6">
        <h2 className="text-base font-semibold text-white mb-1">Liquid Bitcoin Configuration</h2>
        <p className="text-sm text-gray-500 mb-4">These addresses are fixed after onboarding and cannot be changed.</p>
        <div className="space-y-4">
          <CopyField label="Your Destination Wallet" value={settings.merchant_dest_addr} />
          <CopyField label="Relay Contract Address" value={settings.relay_contract_addr} />
          <CopyField label="Customer Covenant Address" value={settings.customer_covenant_addr} />
          <CopyField label="Merchant Escrow Address" value={settings.escrow_contract_addr} />
          <CopyField label="Oracle Public Key" value={settings.oracle_pubkey} />
        </div>
      </section>

      {/* Network */}
      <section className="card mb-6">
        <h2 className="text-base font-semibold text-white mb-4">Network</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-200">Current Network</div>
            <div className="text-xs text-gray-500 mt-0.5">Switch to mainnet before accepting real payments</div>
          </div>
          <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
            <span className="text-sm font-medium text-yellow-400">Testnet</span>
          </div>
        </div>
      </section>

      {/* API Keys — placeholder */}
      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-white">API Keys</h2>
            <p className="text-sm text-gray-500 mt-0.5">Programmatic access to your merchant account</p>
          </div>
          <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded-full">Coming Soon</span>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center">
          <svg className="w-8 h-8 text-gray-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <p className="text-sm text-gray-500">API key management will be available in an upcoming release.</p>
        </div>
      </section>
    </div>
  );
}
